import { Ban, Plus, Save } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useAdminAuth } from '../auth/AdminAuthProvider'
import { disableAdminAccountRemote, fetchAdminAccounts, setAdminRoleRemote } from '../lib/adminAccounts'
import { ADMIN_ROLES, ADMIN_ROLE_LABELS } from '../../../../packages/types/admin'

const EMPTY_FORM = { targetUid: '', role: 'viewer', displayName: '' }

export default function AdminRolesPage() {
  const auth = useAdminAuth()
  const isOwner = auth.role === 'owner'
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [form, setForm] = useState(EMPTY_FORM)

  async function load() {
    setLoading(true); setError('')
    try {
      const token = await auth.getIdToken()
      setAccounts(await fetchAdminAccounts(token))
    } catch (err) { setError(err.message) } finally { setLoading(false) }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 마운트 시 1회만 불러온다(다른 admin 페이지와 동일한 패턴).
  useEffect(() => { load() }, [])

  async function submitRole(event) {
    event.preventDefault()
    if (!form.targetUid.trim() || !form.displayName.trim()) { setError('UID와 표시 이름을 입력하세요.'); return }
    setBusy(true); setMessage(''); setError('')
    try {
      const token = await auth.getIdToken()
      await setAdminRoleRemote(token, form)
      setForm(EMPTY_FORM)
      await load()
      setMessage(`${form.targetUid}의 역할을 ${ADMIN_ROLE_LABELS[form.role]}(으)로 설정했습니다. 재로그인 또는 토큰 갱신 후 반영됩니다.`)
    } catch (err) { setError(err.message) } finally { setBusy(false) }
  }

  async function disable(account) {
    if (!window.confirm(`${account.displayName || account.uid} 계정을 비활성화할까요? 즉시 로그아웃되고 재로그인할 수 없습니다.`)) return
    setBusy(true); setMessage(''); setError('')
    try {
      const token = await auth.getIdToken()
      await disableAdminAccountRemote(token, account.uid)
      await load()
      setMessage(`${account.displayName || account.uid} 계정을 비활성화했습니다.`)
    } catch (err) { setError(err.message) } finally { setBusy(false) }
  }

  function editExisting(account) {
    setForm({ targetUid: account.uid, role: account.role, displayName: account.displayName || '' })
  }

  return (
    <>
      <header className="page-header"><div><p className="eyebrow">시스템</p><h1>관리자 관리</h1><p>관리자 계정의 상태와 접근 수준을 관리합니다. 변경 내용은 작업 기록에 남습니다.</p></div></header>

      {error && <div className="error-state" style={{ marginBottom: 16 }}>{error}</div>}
      {message && <div className="temporary-warning" style={{ marginBottom: 16 }}>{message}</div>}
      {!isOwner && <div className="conflict-warning">현재 role({auth.role})은 조회만 가능합니다. 역할 변경·비활성화는 owner 권한이 필요합니다.</div>}

      {isOwner && (
        <section className="panel" style={{ padding: 16, marginBottom: 16 }}>
          <h2 style={{ margin: '0 0 12px', fontSize: 14 }}>관리자 추가 / 역할 변경</h2>
          <form onSubmit={submitRole} className="field-grid">
            <div className="field"><label htmlFor="targetUid">대상 UID</label><input id="targetUid" className="input" value={form.targetUid} onChange={e => setForm(f => ({ ...f, targetUid: e.target.value }))} placeholder="kakao:1234567890" /></div>
            <div className="field"><label htmlFor="displayName">표시 이름</label><input id="displayName" className="input" value={form.displayName} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))} placeholder="홍길동" /></div>
            <div className="field"><label htmlFor="role">역할</label>
              <select id="role" className="select" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                {ADMIN_ROLES.map(role => <option key={role} value={role}>{ADMIN_ROLE_LABELS[role]}</option>)}
              </select>
            </div>
            <div className="field" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button className="button primary" type="submit" disabled={busy}><Save size={16} />저장</button>
              {form.targetUid && <button className="button secondary" type="button" style={{ marginLeft: 8 }} disabled={busy} onClick={() => setForm(EMPTY_FORM)}>취소</button>}
            </div>
          </form>
          <p className="field-help">기존 UID를 입력하면 역할이 바뀌고, 새 UID를 입력하면 새 관리자가 추가됩니다. 자기 자신의 역할은 낮출 수 없습니다.</p>
        </section>
      )}

      <section className="panel table-wrap">
        {loading ? <div className="empty-state">불러오는 중…</div> : accounts.length === 0 ? <div className="empty-state">등록된 관리자가 없습니다.</div> : (
          <table className="data-table"><thead><tr><th>UID</th><th>이름</th><th>역할</th><th>상태</th>{isOwner && <th>작업</th>}</tr></thead><tbody>
            {accounts.map(account => (
              <tr key={account.uid}>
                <td>{account.uid}</td>
                <td>{account.displayName || '—'}</td>
                <td><span className="status-badge published">{ADMIN_ROLE_LABELS[account.role] || account.role}</span></td>
                <td><span className={`status-badge ${account.enabled === false ? 'ended' : 'published'}`}>{account.enabled === false ? '비활성' : '활성'}</span></td>
                {isOwner && (
                  <td>
                    <button className="icon-button" type="button" onClick={() => editExisting(account)} aria-label="역할 편집"><Plus size={16} /></button>
                    {account.enabled !== false && account.uid !== auth.user.uid && (
                      <button className="icon-button" type="button" onClick={() => disable(account)} aria-label="비활성화"><Ban size={16} /></button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody></table>
        )}
      </section>
    </>
  )
}
